import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  FileQuestion,
  Sparkles,
  Layers,
  Gamepad2,
  ShieldCheck,
  Building2,
  CheckCircle2,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { networkID } from "../../../../../static/utils";
import {
  getNetworkCurrency,
  getNetworkName,
  DEFAULT_TOKEN_SYMBOL,
} from "../../../../../static/Constants";
import HelpEmptyState from "./HelpEmptyState";

export const getFaqData = () => {
  const currentNetworkName = getNetworkName(networkID);
  const currentCurrency = getNetworkCurrency(networkID);

  return [
    {
      id: 1,
      category: "general",
      categoryLabel: "General",
      question: "What is the AutoBET or AutoBET Lottery Ecosystem?",
      solution:
        "AutoBET Lottery Ecosystem is an advanced Web3 platform that utilizes blockchain technology and smart contracts to provide a transparent, provably fair, and decentralized lottery experience. It offers pre-whitelisted lottery products, automated ticket purchases, Chainlink VRF random draws, instant on-chain payouts, and a secure environment for participants worldwide.",
    },
    {
      id: 2,
      category: "general",
      categoryLabel: "General",
      question: "How does the AutoBET Lottery Ecosystem work?",
      solution:
        "AutoBET operates on decentralized smart contract networks (such as Polygon and BSC), ensuring complete transparency. Participants can purchase tickets in supported cryptocurrencies or launch customized community lotteries using our B2C white-label creator products. Smart contracts manage ticket sales, verifiable random number generation, and instant prize disbursements directly to winner wallets without intermediaries.",
    },
    {
      id: 3,
      category: "general",
      categoryLabel: "General",
      question: "Which cryptocurrencies are accepted for ticket purchases?",
      solution: `AutoBET Lottery currently accepts ${currentNetworkName} (${currentCurrency}) cryptocurrency. The platform aims to expand its supported cryptocurrencies in the future to provide more options for participants including implementation of its native ${DEFAULT_TOKEN_SYMBOL} token.`,
    },
    {
      id: 4,
      category: "general",
      categoryLabel: "General",
      question: "What are the key advantages of using AutoBET Lottery?",
      type: "multiple",
      solution: [
        {
          subSolution: "AutoBET Lottery provides cutting-edge Web3 advantages over traditional lotteries:",
        },
        {
          subTitle: "100% On-Chain Transparency",
          subSolution:
            "All lottery transactions, ticket sales, and drawings are recorded permanently on the blockchain, eliminating fraud or hidden manipulation.",
        },
        {
          subTitle: "Non-Custodial Security",
          subSolution:
            "Funds are locked in verified smart contracts. Neither the platform nor node operators hold custody of your prize pool.",
        },
        {
          subTitle: "Automated Execution",
          subSolution:
            "Smart contracts automate round management, rollover accumulation, and winning disbursements without human intervention.",
        },
        {
          subTitle: "Instant Direct Payouts",
          subSolution:
            "Winners receive prizes immediately in cryptocurrency directly into their connected Web3 wallets.",
        },
        {
          subTitle: "Global Web3 Accessibility",
          subSolution:
            "Anyone with a Web3 wallet can participate seamlessly without traditional banking barriers.",
        },
      ],
    },
    {
      id: 5,
      category: "gameplay",
      categoryLabel: "Gameplay & Rules",
      question: "How can I participate in the AutoBET Lottery?",
      solution:
        "To participate in the AutoBET Lottery, you need simply need to have a compatible digital wallet (such as MetaMask, Trust Wallet, or WalletConnect). Lottery creators can register to create lotteries with a standard 10% fee on total fundraising target volumes. Players can purchase lottery tickets using supported cryptocurrencies, and your tickets will be automatically entered into the draw. Make sure to check the rules and regulations of your jurisdiction regarding participation in lotteries.",
    },
    {
      id: 6,
      category: "gameplay",
      categoryLabel: "Gameplay & Rules",
      question: "How are winners selected in the AutoBET Lottery?",
      solution:
        "Winners in the AutoBET Lottery are randomly selected using a transparent and secure algorithm. The smart contracts generate random numbers based on predetermined factors, ensuring fairness and impartiality in the draw using the latest Chainlink VRF technology. The selection process is automated and cannot be influenced or manipulated by any party.",
    },
    {
      id: 7,
      category: "gameplay",
      categoryLabel: "Gameplay & Rules",
      question: "How are prizes distributed to winners?",
      solution:
        "Prizes are distributed instantly to the digital wallets of the winners. Upon the completion of the draw, the smart contracts automatically transfer the respective prize amounts (inclusive of 10% taxed by the platform) to the wallet addresses associated with the winning tickets. Winners can then access and manage their winnings as per their preference.",
    },
    {
      id: 8,
      category: "gameplay",
      categoryLabel: "Gameplay & Rules",
      question: "Are there any additional fees or player commissions?",
      solution:
        "AutoBET does not charge hidden platform fees to players. Players only pay the displayed ticket entry price plus standard blockchain network gas fees. Lottery creators register with a standard 10% fee on total fundraising target volumes.",
    },
    {
      id: 9,
      category: "gameplay",
      categoryLabel: "Gameplay & Rules",
      question: "Can anyone participate in the AutoBET Lottery Ecosystem?",
      solution:
        "Yes, anyone who meets the legal age requirements in their local jurisdiction can connect a Web3 wallet and participate in open games globally.",
    },
    {
      id: 10,
      category: "security",
      categoryLabel: "Security & Trust",
      question: "Is the AutoBET Lottery Ecosystem secure?",
      solution:
        "Yes, the AutoBET Lottery Ecosystem prioritizes security and utilizes blockchain technology to ensure a secure and tamper-proof environment. The decentralized nature of the platform, combined with smart contracts, enhances transparency and eliminates the possibility of fraud or manipulation. We have plans to engage Smart Contract Audits to be done regularly by our partners.",
    },
    {
      id: 11,
      category: "security",
      categoryLabel: "Security & Trust",
      question: "Can I play the AutoBET Lottery from any country?",
      solution:
        "The availability of the AutoBET Lottery may vary depending on the legal restrictions and regulations of each country. It is essential to check the platform's terms and conditions and comply with the laws of your jurisdiction before participating in the lottery.",
    },
    {
      id: 12,
      category: "security",
      categoryLabel: "Security & Trust",
      question: "Can I trust the AutoBET Lottery platform?",
      solution:
        "AutoBET is trustless by design: you don't need to trust a centralized operator because every rule, ticket sale, Chainlink VRF seed, and payout is verifiable on public blockchain explorers.",
    },
    {
      id: 13,
      category: "security",
      categoryLabel: "Security & Trust",
      question: "Can I remain anonymous when winning on AutoBET?",
      solution:
        "AutoBET does not require traditional KYC or personal information to participate; transactions are tied solely to your public Web3 wallet address. You retain pseudonymity according to your wallet provider and chosen network.",
    },
    // {
    //   id: 18,
    //   category: "partners",
    //   categoryLabel: "Partners & Creators",
    //   question: "How can I contact AutoBET support for assistance?",
    //   solution:
    //     "For official inquiries, partnership proposals, or technical assistance, contact our team directly at info@autobetlottery.com or join our official community channels. Our team strives to respond to inquiries promptly.",
    // },
  ];
};

export const FAQ_DATA = getFaqData();

const FAQ_TABS = [
  { id: "all", label: "All Questions", icon: Layers, color: colors.primary.main },
  { id: "general", label: "General & Platform", icon: Sparkles, color: colors.blue[600] },
  { id: "gameplay", label: "Gameplay & Rules", icon: Gamepad2, color: colors.emerald[600] },
  { id: "security", label: "Security & Fairness", icon: ShieldCheck, color: colors.amber[600] },
  { id: "partners", label: "Partners & Support", icon: Building2, color: colors.purple[600] },
];

const HelpFaqSection = ({ searchQuery = "", onClearSearch }) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedId, setExpandedId] = useState(false);

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    setExpandedId(isExpanded ? panelId : false);
  };

  const filteredFaqs = useMemo(() => {
    let result = FAQ_DATA;

    if (activeCategory !== "all") {
      result = result.filter((f) => f.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((f) => {
        const questionMatch = f.question.toLowerCase().includes(q);
        const solutionMatch =
          typeof f.solution === "string"
            ? f.solution.toLowerCase().includes(q)
            : Array.isArray(f.solution) &&
              f.solution.some(
                (item) =>
                  (item.subTitle && item.subTitle.toLowerCase().includes(q)) ||
                  (item.subSolution && item.subSolution.toLowerCase().includes(q))
              );
        return questionMatch || solutionMatch;
      });
    }

    return result;
  }, [activeCategory, searchQuery]);

  const countsByCategory = useMemo(() => {
    const counts = { all: FAQ_DATA.length };
    FAQ_DATA.forEach((f) => {
      counts[f.category] = (counts[f.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <Box id="faq-section" sx={{ mb: 6 }}>
      {/* Card Header with Section Title & Category Tabs */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack spacing={2.5}>
          {/* Top Title & Badge */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.12)",
                }}
              >
                <FileQuestion size={22} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.25rem" }}>
                  Frequently Asked Questions
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.875rem" }}>
                  Answers to common questions about gameplay, rules, security, and creators
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                px: 1.75,
                py: 0.6,
                borderRadius: borderRadiusPx.full,
                bgcolor: colors.emerald[50],
                border: `1px solid ${colors.emerald[100]}`,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.emerald[700], fontWeight: 700, fontSize: "0.75rem" }}>
                Showing {filteredFaqs.length} of {FAQ_DATA.length} Questions
              </Typography>
            </Box>
          </Stack>

          {/* Category Tabs */}
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            sx={{
              gap: 1.25,
              pt: 0.5,
            }}
          >
            {FAQ_TABS.map((tab) => {
              const isSelected = activeCategory === tab.id;
              const TabIcon = tab.icon;
              const count = countsByCategory[tab.id] || 0;

              return (
                <Box
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: borderRadiusPx.lg,
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                    border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                    boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                    "&:hover": {
                      backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                      borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                    },
                  }}
                >
                  <TabIcon size={16} color={isSelected ? colors.neutral.white : tab.color} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 700 : 600,
                      fontSize: "0.875rem",
                      color: isSelected ? colors.neutral.white : colors.slate[700],
                    }}
                  >
                    {tab.label}
                  </Typography>
                  <Box
                    sx={{
                      px: 0.85,
                      py: 0.15,
                      borderRadius: borderRadiusPx.full,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                      color: isSelected ? colors.neutral.white : colors.slate[700],
                    }}
                  >
                    {count}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Card>

      {/* Accordion FAQ List */}
      {filteredFaqs.length > 0 ? (
        <Stack spacing={2}>
          {filteredFaqs.map((faq) => {
            const isPanelExpanded = expandedId === `panel-${faq.id}`;

            return (
              <Accordion
                key={faq.id}
                expanded={isPanelExpanded}
                onChange={handleAccordionChange(`panel-${faq.id}`)}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: `${borderRadiusPx.lg} !important`,
                  border: `1px solid ${isPanelExpanded ? colors.primary.light : colors.dashboard.cardBorder}`,
                  boxShadow: isPanelExpanded ? customShadows.cardHover : customShadows.cardFlat,
                  backgroundColor: colors.dashboard.cardBg,
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  "&:before": { display: "none" },
                  "&:hover": {
                    borderColor: isPanelExpanded ? colors.primary.main : colors.slate[300],
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: isPanelExpanded ? colors.primary.main : colors.slate[500],
                        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  }
                  sx={{
                    px: { xs: 2.5, sm: 3 },
                    py: 1.5,
                    bgcolor: isPanelExpanded ? colors.blue[50] : colors.background.paper,
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      bgcolor: isPanelExpanded ? colors.blue[50] : colors.slate[50],
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        borderRadius: borderRadiusPx.sm,
                        bgcolor: isPanelExpanded ? colors.primary.main : colors.slate[100],
                        color: isPanelExpanded ? colors.neutral.white : colors.slate[600],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.85rem",
                      }}
                    >
                      Q{faq.id}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "0.95rem", sm: "1.05rem" },
                        color: isPanelExpanded ? colors.primary.dark : colors.slate[900],
                        lineHeight: 1.4,
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: { xs: 2.5, sm: 3 },
                    py: 3,
                    bgcolor: colors.dashboard.cardBg,
                    borderTop: `1px solid ${colors.dashboard.divider}`,
                  }}
                >
                  {typeof faq.solution === "string" ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.slate[700],
                        lineHeight: 1.7,
                        fontSize: "0.95rem",
                      }}
                    >
                      {faq.solution}
                    </Typography>
                  ) : Array.isArray(faq.solution) ? (
                    <Stack spacing={2}>
                      {faq.solution.map((item, subIdx) => {
                        if (item.subSolution && !item.subTitle) {
                          return (
                            <Typography
                              key={subIdx}
                              variant="body1"
                              sx={{
                                color: colors.slate[800],
                                fontWeight: 600,
                                lineHeight: 1.6,
                                fontSize: "0.95rem",
                              }}
                            >
                              {item.subSolution}
                            </Typography>
                          );
                        }

                        return (
                          <Box
                            key={subIdx}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              p: 1.5,
                              borderRadius: borderRadiusPx.md,
                              bgcolor: colors.slate[50],
                              border: `1px solid ${colors.slate[200]}`,
                            }}
                          >
                            <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                              <CheckCircle2
                                size={18}
                                color={colors.primary.main}
                              />
                            </Box>
                            <Box>
                              {item.subTitle && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ fontWeight: 700, color: colors.slate[900], mr: 0.5 }}
                                >
                                  {item.subTitle}:
                                </Typography>
                              )}
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: colors.slate[700], lineHeight: 1.6 }}
                              >
                                {item.subSolution}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: colors.slate[700], lineHeight: 1.6 }}>
                      {String(faq.solution)}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      ) : (
        <HelpEmptyState
          title="No FAQ Results Found"
          description={`No questions matched "${searchQuery}". Please refine your search or clear it to browse all questions.`}
          actionText="Clear Search & Show All Questions"
          onAction={onClearSearch}
        />
      )}
    </Box>
  );
};

HelpFaqSection.propTypes = {
  searchQuery: PropTypes.string,
  onClearSearch: PropTypes.func.isRequired,
};

export default HelpFaqSection;
